import isEqual from 'lodash.isequal';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { MODAL_KEYS } from '_constants/modals';
import { TASK_TYPE } from '_constants/tasks';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import { usePrevious } from '_hooks';
import { selectIsFileTypeEmpty } from '_store/fileTypes/selector';
import { getMergedFileTypeMap } from '_store/fileTypes/slice';
import { showModal } from '_store/modals/slice';
import { selectActiveOrderId } from '_store/order/selector';
import { selectActiveBusinessTaxFilingProductId, selectActivePersonalTaxFilingProductId } from '_store/order/selector';
import { uploadStatement } from '_store/statements/slice';
import { selectData, selectFetchCounter, selectLoading, selectParams } from '_store/tasks/selector';
import { getTasks, refreshParams } from '_store/tasks/slice';
import { selectCustomerId } from '_store/user/selector';

import Paginate from 'components/atomic/Paginate';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import TasksList from './TasksList';

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledBadge = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  min-width: 28px;

  padding-top: ${theme.spaces.small2X}px;
  padding-bottom: ${theme.spaces.small2X}px;
  padding-left: ${theme.spaces.smallX + 1}px;
  padding-right: ${theme.spaces.smallX + 1}px;

  border: 1px solid ${hexToRGBA(theme.colors.indigo.primary, 0.08)};
  border-radius: 16px;
  background: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};
`;

const StyledPaginateContainer = styled.div``;

const TasksView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const orderId = useSelector(selectActiveOrderId);
  const loading = useSelector(selectLoading);
  const fetchCounter = useSelector(selectFetchCounter);
  const data = useSelector(selectData);
  const params = useSelector(selectParams);
  const prevParams = usePrevious(params);

  const activeBusinessTaxFilingProductId = useSelector(selectActiveBusinessTaxFilingProductId);
  const activePersonalTaxFilingProductId = useSelector(selectActivePersonalTaxFilingProductId);
  const isFileTypeEmpty = useSelector(selectIsFileTypeEmpty);

  const handlePageChange = (page) => {
    dispatch(refreshParams({ page }));
  };

  const handleActionClick = useCallback(
    (data = {}, file) => {
      const { type, productId, fileRequest } = data;

      if (type === TASK_TYPE.fileRequest) {
        dispatch(uploadStatement({ customerId, productId, statementId: fileRequest?.id, file }));
      }

      if (type === TASK_TYPE.textRequest) {
        dispatch(showModal({ key: MODAL_KEYS.taskResponseSend, data }));
      }
    },
    [customerId]
  );

  useEffect(() => {
    if (customerId && orderId && fetchCounter && !isEqual(prevParams, params)) {
      dispatch(getTasks({ customerId, orderId }));
    }
  }, [customerId, orderId, fetchCounter, prevParams, params]);

  useEffect(() => {
    if (customerId && orderId && !fetchCounter) {
      dispatch(getTasks({ customerId, orderId }));
    }
  }, [customerId, orderId, fetchCounter]);

  useEffect(() => {
    if (customerId && isFileTypeEmpty) {
      const productIds = [activeBusinessTaxFilingProductId, activePersonalTaxFilingProductId].filter((id) => id);
      dispatch(getMergedFileTypeMap({ customerId, productIds }));
    }
  }, [customerId, activeBusinessTaxFilingProductId, activePersonalTaxFilingProductId, isFileTypeEmpty]);

  return (
    <StyledCard>
      <StyledHeader>
        <StyledTitleContainer>
          <Text inline size={theme.fontSizes.medium} weight={600}>
            Tasks
          </Text>
          <StyledBadge>
            <Text inline size={theme.fontSizes.smallX} weight={600} color={theme.colors.indigo.primary}>
              {data.totalResults || 0}
            </Text>
          </StyledBadge>
        </StyledTitleContainer>
      </StyledHeader>
      <TasksList loading={!data.elements?.length && loading} items={data.elements} onActionClick={handleActionClick} />
      <StyledPaginateContainer>
        <Paginate
          pageRangeDisplayed={0}
          pageCount={data?.totalPages}
          currentPage={data?.page}
          onPageChange={handlePageChange}
        />
      </StyledPaginateContainer>
    </StyledCard>
  );
};

export default TasksView;
